import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { X, CheckCircle, Minus, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

// Shared Security Checks table used by both Google Workspace and Microsoft 365
// posture pages. Display-only. Defaults reproduce the Google Workspace UI
// exactly; callers pass field adapters for provider-specific values.

const SEV_BADGE = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  informational: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };

const SeverityBadge = ({ severity, testId }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border capitalize ${SEV_BADGE[severity] || SEV_BADGE.low}`} data-testid={testId}>
    {severity}
  </span>
);

const defaultFixShort = (breadcrumb) => {
  if (!breadcrumb) return null;
  return breadcrumb.replace(/^Google Admin Console\s*→\s*/i, '').trim();
};

const SecurityChecksTable = ({
  posture,
  testIdPrefix = 'gw',
  openLabel = 'Open in Google Admin',
  passedSeverityLabel = 'informational',
  getOpenUrl = (f) => f.fix_url,
  getFixShort = (f) => defaultFixShort(f.fix_breadcrumb || f.how_to_fix?.breadcrumb),
  getWhatWeFound = (f) => f.proof,
  getHowToFix = (f) => f.what_would_fix,
  getWhere = (f) => f.how_to_fix?.breadcrumb,
  getNaFixShort = () => null,
  getNaHowToFix = () => null,
  getFrameworksSatisfied = null,
}) => {
  const [showPassed, setShowPassed] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const findings = posture?.findings || [];
  const isFailStatus = (f) => f.status === 'fail' || f.status === 'failed';
  const isNaStatus = (f) => f.status === 'not_evaluated';
  const failed = findings.filter(isFailStatus);
  const passedCount = findings.filter((f) => f.status === 'pass').length;

  // Default view: failing + not-assessed. "Show passed" adds passing rows.
  // Order: failures first (by severity), then not-assessed, then passed.
  const groupRank = (f) => (isFailStatus(f) ? 0 : isNaStatus(f) ? 1 : 2);
  const rows = (showPassed ? findings : findings.filter((f) => isFailStatus(f) || isNaStatus(f)))
    .slice()
    .sort((a, b) => groupRank(a) - groupRank(b)
      || (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9));

  const toggle = (id) => setExpanded((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <Card className="border-slate-200" data-testid={`${testIdPrefix}-checks-table-card`}>
      <CardContent className="p-0">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-red-700">{failed.length} failed</span>
            <span className="text-slate-400"> · </span>
            <span>{passedCount} passed</span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid={`${testIdPrefix}-checks-show-passed-toggle`}>
            <input type="checkbox" checked={showPassed} onChange={(e) => setShowPassed(e.target.checked)} className="rounded border-slate-300" />
            Show passed controls
          </label>
        </div>

        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500" data-testid={`${testIdPrefix}-checks-empty`}>
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            {showPassed ? 'No checks found.' : 'No failing checks — every control passed. 🎉'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-14" />
                <col className="w-[30%]" />
                <col className="w-28" />
                <col className="w-[18%]" />
                <col className="w-[26%]" />
                <col className="w-12" />
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Status', 'Control', 'Severity', 'Frameworks', 'Fix', ''].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((f) => {
                  const rowId = f.finding_id || f.rule_key;
                  const isOpen = expanded.has(rowId);
                  const isFail = isFailStatus(f);
                  const isNa = isNaStatus(f);
                  const fix = getFixShort(f);
                  const openUrl = getOpenUrl(f);
                  const whatWeFound = getWhatWeFound(f);
                  const howToFix = getHowToFix(f);
                  const where = getWhere(f);
                  const naFix = isNa ? getNaFixShort(f) : null;
                  const naHowToFix = isNa ? getNaHowToFix(f) : null;
                  const controlIds = f.control_ids || [];
                  const frameworksSatisfied = getFrameworksSatisfied ? getFrameworksSatisfied(f) : null;
                  return (
                    <React.Fragment key={rowId}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(rowId)} data-testid={`${testIdPrefix}-check-row-${f.rule_key}`}>
                        <td className="px-4 py-3 align-top">
                          {isFail
                            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700" title="Failed"><X className="w-3 h-3" /></span>
                            : isNa
                            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-500" title="Not assessed"><Minus className="w-3 h-3" /></span>
                            : <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700" title="Passed"><CheckCircle className="w-3 h-3" /></span>}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 align-top break-words">{f.label}</td>
                        <td className="px-4 py-3 align-top">
                          {isNa
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-slate-100 text-slate-500 border-slate-200" data-testid={`${testIdPrefix}-check-severity-${f.rule_key}`}>Not assessed</span>
                            : <SeverityBadge severity={isFail ? f.severity : (passedSeverityLabel ?? f.severity)} testId={`${testIdPrefix}-check-severity-${f.rule_key}`} />}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 align-top break-words">{f.frameworks?.length ? f.frameworks.join(', ') : '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-600 align-top break-words">{isFail && fix ? fix : (isNa && naFix ? naFix : '—')}</td>
                        <td className="px-4 py-3 text-right align-top">
                          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60" data-testid={`${testIdPrefix}-check-detail-${f.rule_key}`}>
                          <td colSpan={6} className="px-4 py-3">
                            <div className="text-xs text-slate-600 space-y-2 max-w-3xl">
                              {whatWeFound && <p><span className="font-medium text-slate-700">What we found:</span> {whatWeFound}</p>}
                              {isFail && howToFix && <p><span className="font-medium text-slate-700">How to fix:</span> {howToFix}</p>}
                              {isNa && naHowToFix && <p data-testid={`${testIdPrefix}-check-na-howtofix-${f.rule_key}`}><span className="font-medium text-slate-700">How to fix:</span> {naHowToFix}</p>}
                              {where && <p><span className="font-medium text-slate-700">Where:</span> {where}</p>}
                              {getFrameworksSatisfied ? (
                                frameworksSatisfied?.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 items-center">
                                    <span className="font-medium text-slate-700">Frameworks satisfied:</span>
                                    {frameworksSatisfied.map((fw) => (
                                      <span key={fw} className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[11px] text-slate-600">{fw}</span>
                                    ))}
                                  </div>
                                )
                              ) : (
                                controlIds.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 items-center">
                                    <span className="font-medium text-slate-700">Mapped controls:</span>
                                    {controlIds.map((cid) => (
                                      <span key={cid} className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[11px] text-slate-600">{cid}</span>
                                    ))}
                                  </div>
                                )
                              )}
                              {openUrl && (
                                <a href={openUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                                  {openLabel} <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecurityChecksTable;
